import type { GeoPath, GeoPermissibleObjects, GeoProjection } from 'd3-geo';
import { geoCentroid } from 'd3-geo';
import { scaleSequential, scaleSqrt } from 'd3-scale';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import max from 'lodash/max';
import min from 'lodash/min';
import replace from 'lodash/replace';
import toLower from 'lodash/toLower';
import React, { useMemo } from 'react';

import {
  ALPHA3_TO_NUMERIC,
  COLOR_INTERPOLATORS,
  DEFAULT_FLOW_COLORS,
  DEFAULT_ROUTE_COLOR,
  NUMERIC_TO_ALPHA3,
  VESSEL_STATUS_COLORS,
} from './constants';
import type { ChoroplethItem, FlowItem, MarkerItem, RouteItem, VesselItem } from './useMapData';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface TooltipState {
  x: number;
  y: number;
  content: React.ReactNode;
}

type TooltipUpdater = TooltipState | null | ((prev: TooltipState | null) => TooltipState | null);

interface LayerBaseProps {
  projection: GeoProjection;
  onTooltip: (tooltip: TooltipUpdater) => void;
}

// ---------------------------------------------------------------------------
// ChoroplethLayer — country fills by value
// ---------------------------------------------------------------------------

interface ChoroplethLayerProps extends LayerBaseProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  features: Array<{ id: string; properties: Record<string, any>; type: string; geometry: any }>;
  path: GeoPath<unknown, GeoPermissibleObjects>;
  data: ChoroplethItem[];
  colorScheme: string;
  landColor: string;
  borderColor: string;
}

function buildChoroplethLookup (data: ChoroplethItem[]): Record<string, ChoroplethItem> {
  const lookup: Record<string, ChoroplethItem> = {};
  for (const item of data) {
    const numericId = get(ALPHA3_TO_NUMERIC, item.country);
    if (numericId) {
      lookup[numericId] = item;
    }
  }
  return lookup;
}

export const ChoroplethLayer = React.memo(function ChoroplethLayer ({
  features,
  path,
  data,
  colorScheme,
  landColor,
  borderColor,
  onTooltip,
}: ChoroplethLayerProps) {
  const lookup = useMemo(() => buildChoroplethLookup(data), [data]);

  const values = map(data, 'value');
  const domain: [number, number] = [min(values) ?? 0, max(values) ?? 1];

  const interpolator = get(COLOR_INTERPOLATORS, colorScheme, COLOR_INTERPOLATORS.blues);
  const colorScale = scaleSequential(interpolator).domain(domain);

  return (
    <g className="choropleth-layer">
      {map(features, (feat, featIndex) => {
        const id = String(feat.id);
        const item = lookup[id];
        const fill = item ? colorScale(item.value) : landColor;
        const countryName = get(feat, 'properties.name', NUMERIC_TO_ALPHA3[id] ?? id);

        return (
          <path
            // A few world-atlas features (e.g. Kosovo, Northern Cyprus) have no id, so id alone is not
            // a unique key.
            key={isNil(feat.id) ? `feature-${featIndex}` : id}
            d={path(feat as GeoPermissibleObjects) ?? ''}
            fill={fill}
            stroke={borderColor}
            strokeWidth={0.5}
            className="transition-opacity hover:opacity-80"
            onMouseEnter={(e) => {
              onTooltip({
                x: e.clientX,
                y: e.clientY,
                content: item ? (
                  <div>
                    <div className="font-medium">{countryName}</div>
                    <div className="text-muted-foreground text-xs">
                      {item.label ?? item.value.toLocaleString()}
                    </div>
                  </div>
                ) : (
                  <div className="font-medium">{countryName}</div>
                ),
              });
            }}
            onMouseMove={(e) => {
              // Update position only — preserve existing tooltip content
              onTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
            }}
            onMouseLeave={() => onTooltip(null)}
          />
        );
      })}
    </g>
  );
});

// ---------------------------------------------------------------------------
// FlowLayer — sea-lane-like arcs between country centroids
// ---------------------------------------------------------------------------

interface FlowLayerProps extends LayerBaseProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  features: Array<{ id: string; geometry: any }>;
  data: FlowItem[];
  flowColors?: Record<string, string>;
  highlightedFlowKey?: string;
}

/**
 * Matches a flow to a companion table row for highlighting. Falls back to
 * the country codes when a flow has no explicit port coordinates, but
 * prefers lat/lon when present - multiple port-level flows can share the
 * same origin/destination country + group (e.g. two Saudi terminals both
 * shipping crude to China), which would otherwise collide on one key.
 */
function flowKey (flow: FlowItem): string {
  const origin = !isNil(flow.originLat) && !isNil(flow.originLon)
    ? `${flow.originLat},${flow.originLon}`
    : flow.origin;
  const destination = !isNil(flow.destinationLat) && !isNil(flow.destinationLon)
    ? `${flow.destinationLat},${flow.destinationLon}`
    : flow.destination;
  return `${origin}-${destination}-${flow.group ?? 'default'}`;
}

function buildCentroidLookup (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  features: Array<{ id: string; geometry: any }>,
): Record<string, [number, number]> {
  const lookup: Record<string, [number, number]> = {};
  for (const feat of features) {
    const centroid = geoCentroid(feat as GeoPermissibleObjects);
    if (isFinite(centroid[0]) && isFinite(centroid[1])) {
      lookup[String(feat.id)] = centroid;
    }
  }
  return lookup;
}

/** Wraps a longitude back into [-180, 180] before handing it to the projection. */
function normalizeLon (lon: number): number {
  return ((lon % 360) + 540) % 360 - 180;
}

/**
 * Builds a stylized ship-lane-like arc rather than a true great circle. A
 * geodesic is the *shortest* path on a sphere, which for two points at
 * similar latitude bulges toward the pole once projected - that's the
 * familiar flight-path curve, and it happily cuts straight across whatever
 * continent sits between the endpoints (e.g. Saudi Arabia -> China through
 * Central Asia). Ships can't do that; they hug ocean lanes, which for most
 * long-haul oil trade run through the tropical/equatorial bands (Arabian
 * Sea, Indian Ocean, South China Sea, tropical Atlantic).
 *
 * This is an approximation, not real maritime routing (no coastline or
 * strait awareness - see searoute-js for that): it draws a quadratic Bezier
 * through the midpoint pulled toward the equator, which visually tracks
 * closer to real sea lanes for the common cases without the poleward bulge.
 * Some specific routes may still clip a coastline or peninsula.
 */
function seaLikeArcPath (
  from: [number, number],
  to: [number, number],
  projection: GeoProjection,
): string {
  let [lon1] = from;
  let [lon2] = to;
  const [, lat1] = from;
  const [, lat2] = to;

  // Antimeridian: go the short way around instead of across the date line.
  if (lon2 - lon1 > 180) lon2 -= 360;
  else if (lon1 - lon2 > 180) lon1 -= 360;

  const midLon = (lon1 + lon2) / 2;
  const midLat = (lat1 + lat2) / 2;
  const EQUATOR_PULL = 0.35;
  const bulgedLat = midLat * (1 - EQUATOR_PULL);

  const points: string[] = [];
  for (let t = 0; t <= 1; t += 0.05) {
    const oneMinusT = 1 - t;
    const lon = oneMinusT ** 2 * lon1 + 2 * oneMinusT * t * midLon + t ** 2 * lon2;
    const lat = oneMinusT ** 2 * lat1 + 2 * oneMinusT * t * bulgedLat + t ** 2 * lat2;
    const projected = projection([normalizeLon(lon), lat]);
    if (projected) {
      points.push(`${points.length === 0 ? 'M' : 'L'}${projected[0]},${projected[1]}`);
    }
  }
  return points.join('');
}

/** A resolved arc endpoint, in [lon, lat] plus which source it came from (for the node layer below). */
interface ResolvedEndpoint {
  lonLat: [number, number];
  key: string;
}

/** SVG id fragments can't contain most punctuation - strip it so any group name is a safe marker id. */
function arrowMarkerId (group: string): string {
  return `flow-arrow-${replace(group, /[^a-zA-Z0-9]/g, '')}`;
}

function resolveEndpoint (
  lat: number | undefined,
  lon: number | undefined,
  countryCode: string,
  centroids: Record<string, [number, number]>,
): ResolvedEndpoint | null {
  if (!isNil(lat) && !isNil(lon)) {
    return { lonLat: [lon, lat], key: `${lat},${lon}` };
  }
  const numeric = get(ALPHA3_TO_NUMERIC, countryCode);
  const centroid = numeric ? centroids[numeric] : undefined;
  return centroid ? { lonLat: centroid, key: countryCode } : null;
}

export const FlowLayer = React.memo(function FlowLayer ({
  features,
  data,
  projection,
  flowColors,
  highlightedFlowKey,
  onTooltip,
}: FlowLayerProps) {
  const centroids = useMemo(() => buildCentroidLookup(features), [features]);

  const groupColors = useMemo(() => {
    const groups = [...new Set(map(data, (d) => d.group ?? 'default'))];
    const result: Record<string, string> = {};
    for (let i = 0; i < groups.length; i++) {
      result[groups[i]] = get(flowColors, groups[i]) ?? DEFAULT_FLOW_COLORS[i % DEFAULT_FLOW_COLORS.length];
    }
    return result;
  }, [data, flowColors]);

  const values = map(data, 'value');
  const widthScale = scaleSqrt()
    .domain([min(values) ?? 0, max(values) ?? 1])
    .range([1, 6]);

  // Render the highlighted flow last so its arc draws on top of the others
  // it crosses, instead of a dimmed arc drawing over the emphasized one.
  const orderedData = highlightedFlowKey
    ? [...data].sort((a, b) => Number(flowKey(a) === highlightedFlowKey) - Number(flowKey(b) === highlightedFlowKey))
    : data;

  // Endpoint nodes - a small dot at every distinct resolved coordinate
  // (whether from real port lat/lon or a country centroid fallback), sized
  // by total connected volume. Gives the eye a resting point instead of
  // arcs seeming to originate from nowhere, and disambiguates the multiple
  // arcs that now fan out of a single country when port coordinates put
  // them at different points.
  const nodes = useMemo(() => {
    const byKey = new Map<string, { lonLat: [number, number]; total: number }>();
    for (const flow of data) {
      const origin = resolveEndpoint(flow.originLat, flow.originLon, flow.origin, centroids);
      const dest = resolveEndpoint(flow.destinationLat, flow.destinationLon, flow.destination, centroids);
      if (origin) {
        const existing = byKey.get(origin.key);
        byKey.set(origin.key, { lonLat: origin.lonLat, total: (existing?.total ?? 0) + flow.value });
      }
      if (dest) {
        const existing = byKey.get(dest.key);
        byKey.set(dest.key, { lonLat: dest.lonLat, total: (existing?.total ?? 0) + flow.value });
      }
    }
    return [...byKey.entries()];
  }, [data, centroids]);

  const nodeTotals = map(nodes, ([, n]) => n.total);
  const nodeRadiusScale = scaleSqrt()
    .domain([min(nodeTotals) ?? 0, max(nodeTotals) ?? 1])
    .range([2, 7]);

  return (
    <g className="flow-layer">
      <defs>
        {map(Object.entries(groupColors), ([group, color]) => (
          <marker
            key={group}
            id={arrowMarkerId(group)}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L10,5 L0,10 Z" fill={color} />
          </marker>
        ))}
      </defs>
      {map(orderedData, (flow, i) => {
        const origin = resolveEndpoint(flow.originLat, flow.originLon, flow.origin, centroids);
        const dest = resolveEndpoint(flow.destinationLat, flow.destinationLon, flow.destination, centroids);
        if (!origin || !dest) return null;

        const d = seaLikeArcPath(origin.lonLat, dest.lonLat, projection);
        if (!d) return null;

        const group = flow.group ?? 'default';
        const color = groupColors[group];
        const isHighlighted = highlightedFlowKey === flowKey(flow);
        const isDimmed = Boolean(highlightedFlowKey) && !isHighlighted;
        const baseWidth = widthScale(flow.value) + (isHighlighted ? 2 : 0);
        const baseOpacity = isDimmed ? 0.12 : isHighlighted ? 1 : 0.6;

        return (
          <path
            key={`${flow.origin}-${flow.destination}-${i}`}
            d={d}
            fill="none"
            stroke={color}
            strokeWidth={baseWidth}
            strokeOpacity={baseOpacity}
            strokeLinecap="round"
            markerEnd={`url(#${arrowMarkerId(group)})`}
            // Widen stroke on hover for easier interaction
            className="transition-all hover:stroke-opacity-90"
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => {
              // Grow stroke via inline style — CSS hover can't target strokeWidth
              const el = e.currentTarget;
              el.setAttribute('stroke-width', String(baseWidth + 2));
              el.setAttribute('stroke-opacity', '0.9');
              onTooltip({
                x: e.clientX,
                y: e.clientY,
                content: (
                  <div>
                    <div className="font-medium">
                      {flow.origin} &rarr; {flow.destination}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {flow.label ?? flow.value.toLocaleString()}
                    </div>
                    {flow.group && (
                      <div className="text-muted-foreground text-xs">{flow.group}</div>
                    )}
                  </div>
                ),
              });
            }}
            onMouseMove={(e) => {
              onTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              el.setAttribute('stroke-width', String(baseWidth));
              el.setAttribute('stroke-opacity', String(baseOpacity));
              onTooltip(null);
            }}
          />
        );
      })}
      {map(nodes, ([key, node]) => {
        const projected = projection(node.lonLat);
        if (!projected) return null;
        return (
          <circle
            key={key}
            cx={projected[0]}
            cy={projected[1]}
            r={nodeRadiusScale(node.total)}
            className="fill-background stroke-muted-foreground"
            strokeWidth={1.2}
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => onTooltip({
              x: e.clientX,
              y: e.clientY,
              content: <div className="text-muted-foreground text-xs">{node.total.toLocaleString()} throughput</div>,
            })}
            onMouseMove={(e) => onTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)}
            onMouseLeave={() => onTooltip(null)}
          />
        );
      })}
    </g>
  );
});

// ---------------------------------------------------------------------------
// RouteLayer — real routed paths (e.g. from a Sea Routes API), distinct from
// FlowLayer's synthetic sea-like arcs above: these coordinates are already a
// real routed polyline, drawn as a plain projected line rather than a bezier.
// ---------------------------------------------------------------------------

interface RouteLayerProps extends LayerBaseProps {
  data: RouteItem[];
}

function routePath (coordinates: [number, number][], projection: GeoProjection): string {
  const points: string[] = [];
  for (const [lon, lat] of coordinates) {
    const projected = projection([normalizeLon(lon), lat]);
    if (projected) {
      points.push(`${points.length === 0 ? 'M' : 'L'}${projected[0]},${projected[1]}`);
    }
  }
  return points.join('');
}

export const RouteLayer = React.memo(function RouteLayer ({
  data,
  projection,
  onTooltip,
}: RouteLayerProps) {
  return (
    <g className="route-layer">
      {map(data, (route, i) => {
        const d = routePath(route.coordinates, projection);
        if (!d) return null;

        const color = route.color ?? DEFAULT_ROUTE_COLOR;
        const last = route.coordinates[route.coordinates.length - 1];
        const endPos = last ? projection([normalizeLon(last[0]), last[1]]) : null;

        return (
          <g key={route.imo ?? `route-${i}`}>
            <path
              d={d}
              fill="none"
              stroke={color}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              strokeOpacity={0.75}
              strokeLinecap="round"
              className="transition-all hover:stroke-opacity-100"
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => {
                e.currentTarget.setAttribute('stroke-width', '2.5');
                onTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  content: (
                    <div>
                      {route.imo && (
                        <div className="font-medium text-[13px] mb-0.5">IMO {route.imo}</div>
                      )}
                      <div className="text-muted-foreground text-xs">
                        {route.label ?? 'Projected route'}
                      </div>
                    </div>
                  ),
                });
              }}
              onMouseMove={(e) => {
                onTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.setAttribute('stroke-width', '1.5');
                onTooltip(null);
              }}
            />
            {endPos && (
              <circle cx={endPos[0]} cy={endPos[1]} r={2.5} fill={color} stroke="#fff" strokeWidth={1} />
            )}
          </g>
        );
      })}
    </g>
  );
});

// ---------------------------------------------------------------------------
// VesselLayer — positioned dots for individual vessels
// ---------------------------------------------------------------------------

interface VesselLayerProps extends LayerBaseProps {
  data: VesselItem[];
}

export const VesselLayer = React.memo(function VesselLayer ({
  data,
  projection,
  onTooltip,
}: VesselLayerProps) {
  return (
    <g className="vessel-layer">
      {map(data, (vessel, i) => {
        const pos = projection([vessel.lon, vessel.lat]);
        if (!pos) return null;

        const statusKey = toLower(vessel.status ?? 'default');
        const color = get(VESSEL_STATUS_COLORS, statusKey, VESSEL_STATUS_COLORS.default);

        return (
          <g key={vessel.imo ?? `vessel-${i}`} style={{ cursor: 'pointer' }}>
            <circle
              cx={pos[0]}
              cy={pos[1]}
              r={4}
              fill={color}
              stroke="#fff"
              strokeWidth={1.5}
              opacity={0.92}
              onMouseEnter={(e) => {
                // Grow circle on hover like the reference
                e.currentTarget.setAttribute('r', '7');
                onTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  content: (
                    <div>
                      {vessel.name && (
                        <div className="font-medium text-[13px] mb-0.5">{vessel.name}</div>
                      )}
                      <div className="text-muted-foreground text-[11px] mb-1">
                        {vessel.status ? `${vessel.status}` : 'Vessel'}
                        {vessel.imo && ` \u00b7 IMO ${vessel.imo}`}
                      </div>
                      {!isNil(vessel.speed) && (
                        <div className="text-xs">
                          {vessel.speed} kn
                          {!isNil(vessel.heading) && ` / ${vessel.heading}\u00B0`}
                        </div>
                      )}
                      {vessel.destination && (
                        <div className="text-xs">
                          &rarr; {vessel.destination}
                        </div>
                      )}
                    </div>
                  ),
                });
              }}
              onMouseMove={(e) => {
                onTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.setAttribute('r', '4');
                onTooltip(null);
              }}
            />
            {/* Heading indicator — short line extending from the dot */}
            {!isNil(vessel.heading) && !isNil(vessel.speed) && vessel.speed > 0.5 && (
              <line
                x1={pos[0]}
                y1={pos[1]}
                // 10px line in heading direction (0 = north, clockwise)
                x2={pos[0] + 10 * Math.sin((vessel.heading * Math.PI) / 180)}
                y2={pos[1] - 10 * Math.cos((vessel.heading * Math.PI) / 180)}
                stroke={color}
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            )}
          </g>
        );
      })}
    </g>
  );
});

// ---------------------------------------------------------------------------
// MarkerLayer — generic positioned points (infrastructure, facilities, etc.)
// ---------------------------------------------------------------------------

const DEFAULT_MARKER_COLOR = '#2563eb';
const DEFAULT_MARKER_SIZE = 6;

interface MarkerLayerProps extends LayerBaseProps {
  data: MarkerItem[];
}

export const MarkerLayer = React.memo(function MarkerLayer ({
  data,
  projection,
  onTooltip,
}: MarkerLayerProps) {
  return (
    <g className="marker-layer">
      {map(data, (marker, i) => {
        const pos = projection([marker.lon, marker.lat]);
        if (!pos) return null;

        const color = marker.color ?? DEFAULT_MARKER_COLOR;
        const r = marker.size ?? DEFAULT_MARKER_SIZE;

        return (
          <g key={`marker-${i}`} style={{ cursor: 'pointer' }}>
            <circle
              cx={pos[0]}
              cy={pos[1]}
              r={r}
              fill={color}
              stroke="#fff"
              strokeWidth={1.5}
              opacity={0.92}
              onMouseEnter={(e) => {
                e.currentTarget.setAttribute('r', String(r + 3));
                onTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  content: (
                    <div>
                      {marker.name && (
                        <div className="font-medium text-[13px] mb-0.5">{marker.name}</div>
                      )}
                      {marker.group && (
                        <div className="text-muted-foreground text-[11px] mb-1">{marker.group}</div>
                      )}
                      {marker.tooltip && (
                        <div className="text-xs">{marker.tooltip}</div>
                      )}
                    </div>
                  ),
                });
              }}
              onMouseMove={(e) => {
                onTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.setAttribute('r', String(r));
                onTooltip(null);
              }}
            />
            {/* Optional text label next to marker */}
            {marker.label && (
              <text
                x={pos[0] + r + 3}
                y={pos[1] - 2}
                fontSize={9}
                fontWeight={500}
                fill={color}
                opacity={0.85}
                style={{ pointerEvents: 'none' }}
              >
                {marker.label}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
});
